/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { AbstractObject, uuid } from '@src/app/model/abstract-object';
import { Forum } from '@src/app/model/forum';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { FormPillComponent, PillColor, PillColors, PillField, PillState } from '@src/app/shared/form/form-pill/form-pill.component';

/** forums.pillColor stores a PillColors key; it is data, so anything else falls back to light grey. */
export function channelColour(pillColor: string): PillColor {
    return Object.prototype.hasOwnProperty.call(PillColors, pillColor) ? pillColor as PillColor : 'lightGrey';
}

/** forums.pillIcon is a Material Symbols name; a channel created before icons existed has none. */
export function channelIcon(pillIcon: string): string {
    return pillIcon || 'forum';
}

export function channelState(forumId: uuid, name: string, pillColor: string, pillIcon?: string): PillState {
    return { id: forumId, name, style: 'infill', color: channelColour(pillColor), icon: channelIcon(pillIcon) };
}

/** Every colour a channel can be given, keyed by what forums.pillColor stores - a picklist and pill states */
export const ChannelColourStates: PillState[] = (Object.keys(PillColors) as PillColor[])
    .map(key => ({ id: key, name: PillColors[key].name, style: 'infill', color: key }));

/**
 * The channel a request was escalated to, from the flat forum columns ServiceRequest::forumPillColumns
 * joins onto every request. Icon only in a table, where it sits beside the other request pills.
 * Named forumName, not forumId, so it neither clashes with a forumId picklist nor renames the docs key.
 */
export function makeRequestChannelPillField(nameOf = (sr: ServiceRequest) => sr.forumName): PillField {
    const field = FormPillComponent.make($localize`Channel`, 'forumId', {
        makeOption: (sr: ServiceRequest) => channelState(sr.forumId, nameOf(sr), sr.forumPillColor, sr.forumPillIcon),
        tableIconOnly: true,
    }).override({ name: 'forumName' });
    field.cellOpts.heading = '';
    field.toolTip = $localize`Escalation Channel`;
    return field;
}

/**
 * For a page about one forum - a document, ballot or post. There is no table to self populate
 * from, so showChannel sets the one state before the value.
 */
export function makeForumChannelPillField(): PillField {
    return FormPillComponent.make($localize`Channel`, 'id', { states: [] });
}

export function showChannel(field: PillField, forum: Forum) {
    const states = forum ? [channelState(forum.id, forum.name, forum.pillColor, forum.pillIcon)] : [];
    field.picklist.items = states as unknown as AbstractObject[];
    field.setValue(forum);
}
